import { useForm, yupResolver, UseFormReturnType } from "@mantine/form";
import {
  CategoryOptionType,
  CreatePrivateSessionInput,
  GetPrivateSessionDocument,
  useCreatePrivateSessionMutation,
  useGetCategoryOptionManyQuery,
  useGetPoliciesQuery,
  useGetPrivateSessionQuery,
  useUpdatePrivateSessionMutation,
} from "src/generated/graphql";
import { useParams } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import { TestContext, boolean, number, object, string } from "yup";
import { memo, useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  Group,
  useMantineTheme,
  Flex,
  Grid,
  Text,
} from "@mantine/core";
import { useModals } from "@mantine/modals";
import { EMPLOYEE_ROLE_OBJ, MESSAGES } from "src/utils/constants";
import FormCard from "src/components/FormCard";
import NumberInput from "src/components/NumberInput/NumberInput";
import Select, { ISelect } from "src/components/Select";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import { useAppContext } from "src/api/AppContext";
import ActionCard from "src/components/ActionCard";
import Switch from "src/components/Switch/Switch";
import ImageUpload from "src/components/ImageUpload";
import MultiSelect from "src/components/MultiSelect";
import InstructorDetail from "./InstructorDetail";
import RoomDetail from "./RoomDetail";
import DurationDetail from "./DurationDetail";
import { generateDurationArray } from "src/utils/utils";
import useWritePermission from "src/hooks/usePermission";

const privateSessionFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should have at least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
  description: string().max(400).notRequired(),
  revenueCategory: string().required("Revenue Category is required"),
  serviceType: string().required("Service Type is required"),
  intervalDuration: number()
    .required("Interval Duration is required")
    .test("is-zero", "Value must be greater than 0", (value) => value !== 0),
  minDuration: number()
    .required("Min Duration is required")
    .test("is-zero", "Value must be greater than 0", (value) => value !== 0)
    .test(
      "is-multiple",
      "Must be a multiple of Duration Step",
      function (this: TestContext, value: number) {
        if (!(value % this.parent.intervalDuration)) return true;
        return false;
      }
    ),
  maxDuration: number()
    .required("MaxDuration is required")
    .test("is-zero", "Value must be greater than 0", (value) => value !== 0)
    .test(
      "is-multiple",
      "Must be a multiple of Duration Step",
      function (this: TestContext, value: number) {
        if (!(value % this.parent.intervalDuration)) return true;
        return false;
      }
    )
    .test(
      "is-greater-than-min",
      "Must be greater than or equal to Min duration",
      function (this: TestContext, value: number) {
        if (!value) return false;
        if (value >= this.parent.minDuration) return true;
        return false;
      }
    ),
  isTaxable: boolean(),
});

interface IProp {
  isEdit?: boolean;
  refetchData?: () => void;
  isLocationSession: boolean;
}
type IFormProps = Omit<
  CreatePrivateSessionInput,
  "revenueCategory" | "serviceType" | "policies" | "organization"
> & {
  revenueCategory: string;
  serviceType: string;
  policies: string[];
  organization: string;
};

export interface ICreatePrivateSessionFormProps
  extends UseFormReturnType<IFormProps, (values: IFormProps) => IFormProps> {}

const PrivateSessionDetail = ({
  isEdit = true,
  refetchData,
  isLocationSession,
}: IProp) => {
  const isAllowWrite = useWritePermission(
    isLocationSession
      ? EMPLOYEE_ROLE_OBJ.locServices
      : EMPLOYEE_ROLE_OBJ.orgService
  );
  const theme = useMantineTheme();
  // const navigate = useNavigate();
  const [visibleCards, setvisibleCards] = useState<string[]>([]);
  const modals = useModals();
  const { sessionId = "" } = useParams();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(isEdit);
  const { currentOrganization, currentLocation } = useAppContext();
  const [revenueArr, setrevenueArr] = useState<ISelect[]>([]);
  const [serviceArr, setserviceArr] = useState<ISelect[]>([]);
  const { data: policiesArr } = useGetPoliciesQuery();
  const [showLoader, setshowLoader] = useState(true);
  useGetCategoryOptionManyQuery({
    onCompleted(data) {
      let _data = data?.categoryOptionMany;
      let _dataRevenue =
        _data
          ?.filter((e) => e.type === CategoryOptionType.Revenue)
          .map((val) => ({
            value: val.id,
            label: val.name,
          })) || [];
      setrevenueArr(_dataRevenue);

      let _dataService =
        _data
          ?.filter((e) => e.type === CategoryOptionType.Service)
          .map((val) => ({
            value: val.id,
            label: val.name,
          })) || [];
      setserviceArr(_dataService);
    },
    skip: !currentOrganization?.id,
    variables: { orgId: currentOrganization?.id || "" },
  });

  const [createSession, { loading: createLoading }] =
    useCreatePrivateSessionMutation({
      onCompleted: (resp) => {
        if (resp.createPrivateSession) {
          modals.closeAll();
          refetchData && refetchData();
          // navigate(`${ROUTES.SESSION}/${resp.createPrivateSession.id}`);
        }
      },
    });

  const [updateSession, { loading: updateLoading }] =
    useUpdatePrivateSessionMutation({
      onCompleted: () => {
        if (showLoader) {
          toggleFormActive();
          form.resetDirty();
        } else {
          setshowLoader(true);
        }
      },
      refetchQueries: [
        { query: GetPrivateSessionDocument, variables: { id: sessionId } },
      ],
    });

  const { data } = useGetPrivateSessionQuery({
    onCompleted: () => {
      resetFormState();
    },
    fetchPolicy: "network-only",
    skip: !sessionId,
    variables: { id: sessionId },
  });

  const form = useForm<IFormProps>({
    initialValues: {
      name: "",
      description: "",
      intervalDuration: 15,
      minDuration: 30,
      maxDuration: 60,
      isEmployeeRequired: true,
      isTaxable: true,
      isPostTax: false,
      organization: "",
      revenueCategory: "",
      serviceType: "",
      policies: [],
      imageUrl: "",
      isOrg: !isLocationSession,
    },
    validate: yupResolver(privateSessionFormSchema),
  });

  const handleSubmit = async (values: IFormProps) => {
    if (!currentOrganization?.id) return;
    let obj = {
      ...values,
      revenueCategory: {
        id: values.revenueCategory,
      },
      serviceType: {
        id: values.serviceType,
      },
      policies: values.policies.map((e) => ({ id: e })),
      organization: { id: currentOrganization.id },
      isPostTax: values.isTaxable ? values.isPostTax : false,
    };

    const openConfirmModal = () =>
      modals.openConfirmModal({
        closeOnClickOutside: false,
        title: "Duration Conflict Exist",
        children: (
          <Text size="sm">
            Changing the step size will adjust existing durations to conform to
            multiples of the new interval. This can affect existing
            meetings/schedules. Would you still like to proceed?
          </Text>
        ),
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onConfirm: () => handleUpdateCreate(),
      });

    const openMessageModal = () =>
      modals.openContextModal("message", {
        closeOnClickOutside: false,
        title: `Duration Conflict Exist`,
        centered: true,
        innerProps: {
          innerText: `Making this change would exclude existing durations. Please remove
          them first before retrying.`,
        },
      });

    const handleUpdateCreate = () => {
      if (isEdit) {
        if (!sessionId) return;
        updateSession({
          variables: {
            data: obj,
            id: sessionId,
          },
        });
      } else {
        createSession({
          variables: {
            data: obj,
          },
        });
      }
    };

    // check for conflicts and open modal based on conflicts
    const { intervalDuration, durations, maxDuration, minDuration } =
      data?.privateSessionOne || {};
    if (durations?.length) {
      if (
        values?.intervalDuration &&
        intervalDuration !== values.intervalDuration
      ) {
        // check if min or max not updated manually along with intervalDuration
        const steps = values.intervalDuration - intervalDuration!;
        const expectedMin =
          minDuration! + (minDuration! / intervalDuration!) * steps;
        const expectedMax =
          maxDuration! + (maxDuration! / intervalDuration!) * steps;
        // if manually updated min or max then check for conflicts
        if (
          (expectedMin !== values.minDuration &&
            durations.find(
              (d) =>
                d.duration + (d.duration / intervalDuration!) * steps <
                values.minDuration!
            )) ||
          (expectedMax !== values.maxDuration &&
            durations.find(
              (d) =>
                d.duration + (d.duration / intervalDuration!) * steps >
                values.maxDuration!
            ))
        ) {
          openMessageModal();
        } else {
          openConfirmModal();
        }
      } else if (
        (values.minDuration !== minDuration &&
          durations.find((d) => d.duration < values?.minDuration!)) ||
        (values.maxDuration !== maxDuration &&
          durations.find((d) => d.duration > values?.maxDuration!))
      ) {
        openMessageModal();
      } else {
        handleUpdateCreate();
      }
    } else {
      handleUpdateCreate();
    }
  };

  const resetFormState = () => {
    if (!data?.privateSessionOne) return;
    const {
      name,
      description,
      intervalDuration,
      minDuration,
      maxDuration,
      serviceType,
      revenueCategory,
      organization,
      imageUrl,
      policies,
      isEmployeeRequired,
      isTaxable,
      isPostTax,
      isOrg,
    } = data?.privateSessionOne;
    let _data = {
      name,
      description,
      intervalDuration,
      minDuration,
      maxDuration,
      serviceType: serviceType.id,
      revenueCategory: revenueCategory.id,
      organization: organization.id,
      imageUrl,
      policies: policies.map((e) => e.id),
      isTaxable,
      isPostTax,
      isEmployeeRequired,
      isOrg,
    };
    form.setValues(_data);
    form.resetDirty(_data);
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState();
    toggleFormActive();
  };

  const handleModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: `Create Instructor Customization`,
      children: (
        <InstructorDetail
          sessionData={data?.privateSessionOne || undefined}
          visibleCards={visibleCards}
          setvisibleCards={setvisibleCards}
          isAllowWrite={isAllowWrite}
          isLocationSession={isLocationSession}
        />
      ),
    });
  }, [data, isAllowWrite, isLocationSession]);

  const handleModalRoom = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: `Create Location Room Selection`,
      children: (
        <RoomDetail
          sessionData={data?.privateSessionOne || undefined}
          visibleCards={visibleCards}
          setvisibleCards={setvisibleCards}
          isAllowWrite={isAllowWrite}
          isOrg={!isLocationSession}
        />
      ),
    });
  }, [data, isAllowWrite, isLocationSession]);

  const [durationsData, setdurationsData] = useState<ISelect[]>([]);
  useEffect(() => {
    let _durations: ISelect[] = [];
    const { minDuration, maxDuration, intervalDuration, durations } =
      data?.privateSessionOne || {};
    if (data?.privateSessionOne) {
      _durations = generateDurationArray(
        minDuration!,
        maxDuration!,
        intervalDuration!
      );
    }
    if (durations?.length) {
      _durations = _durations.filter((d) => {
        const filteredIds = durations?.map((item) => String(item.duration));
        return !filteredIds.includes(d.value);
      });
    }
    setdurationsData(_durations);
  }, [data?.privateSessionOne]);

  // filter already added durations from dropdown selection
  const handleModalDuration = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: `Create Private Session Duration`,
      children: (
        <DurationDetail
          visibleCards={visibleCards}
          setvisibleCards={setvisibleCards}
          sessionData={data?.privateSessionOne || undefined}
          durationsData={durationsData}
          isAllowWrite={isAllowWrite}
        />
      ),
    });
  }, [data, durationsData, isAllowWrite]);

  // round off min/max duration values on intervalChange
  const handleIntervalChange = (num?: number) => {
    const { minDuration, maxDuration, intervalDuration } = form.values;
    const _num = num || intervalDuration || 1;
    form.setFieldValue("intervalDuration", _num);
    if (minDuration && minDuration % _num) {
      form.setFieldValue(
        "minDuration",
        num && intervalDuration && num < intervalDuration
          ? Math.floor(minDuration / _num) * _num
          : Math.ceil(minDuration / _num) * _num
      );
    }
    if (maxDuration && maxDuration % _num) {
      form.setFieldValue(
        "maxDuration",
        num && intervalDuration && num < intervalDuration
          ? Math.floor(maxDuration / _num) * _num
          : Math.ceil(maxDuration / _num) * _num
      );
    }
  };

  const isFormUpdated = form.isDirty() && form.isTouched();
  const isEmpChecked = form.getInputProps("isEmployeeRequired")?.value;
  const stepSize = form.getInputProps("intervalDuration").value;
  const isTaxableChecked = form.getInputProps("isTaxable")?.value;

  const CurrentInstructors =
    useMemo(
      () =>
        isLocationSession
          ? data?.privateSessionOne?.overrides?.filter((employee) =>
              employee.employee.locations.some(
                (location) => location.id === currentLocation?.id
              )
            )
          : data?.privateSessionOne?.overrides,
      [data?.privateSessionOne?.overrides, currentLocation, isLocationSession]
    ) || [];

  const showAddLocation = useMemo(
    () =>
      isLocationSession
        ? !data?.privateSessionOne?.rooms.find(
            (ps) => ps.location.id === currentLocation?.id
          )
        : true,
    [data?.privateSessionOne?.rooms, currentLocation]
  );
  const CurrentRooms =
    useMemo(
      () =>
        isLocationSession
          ? data?.privateSessionOne?.rooms?.filter(
              (e) => e.location.id === currentLocation?.id
            )
          : data?.privateSessionOne?.rooms,
      [data?.privateSessionOne?.rooms, currentLocation, isLocationSession]
    ) || [];

  return (
    <>
      <FormCard
        title={"Basic Info"}
        loading={showLoader && (updateLoading || createLoading)}
        bannerContent={
          showLoader && isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""
        }
        onCancel={handleCancel}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleSubmit(values))}
        isFormActive={isFormActive}
        hideCard={!isEdit}
      >
        <Grid>
          <Grid.Col span={12} md={sessionId ? 5 : 12} sx={{ margin: "auto" }}>
            <ImageUpload
              disabled={isFormActive}
              onReadSuccess={(b64) => form.setFieldValue("imageUrl", b64)}
              src={form.values.imageUrl || ""}
              alt="Image"
              sx={{
                maxHeight: !sessionId
                  ? 200
                  : form.getInputProps("imageUrl").value
                  ? "auto"
                  : 260,
                maxWidth: !sessionId ? 200 : "auto",
                margin: "auto",
                minHeight: !sessionId ? 200 : 260,
              }}
            />
          </Grid.Col>
          <Grid.Col xs={12} md={sessionId ? 7 : 12}>
            <TextInput
              withAsterisk
              label="Name"
              {...form.getInputProps("name")}
              disabled={isFormActive}
              mt={"xs"}
            />

            <NumberInput
              withAsterisk
              maxLength={5}
              label="Duration Step Size"
              {...form.getInputProps("intervalDuration")}
              disabled={isFormActive}
              onChange={handleIntervalChange}
              min={1}
            />

            <Flex gap={"xs"} justify={"space-between"}>
              <NumberInput
                withAsterisk
                label="Min Duration"
                {...form.getInputProps("minDuration")}
                disabled={isFormActive}
                maxLength={8}
                styles={{
                  root: { width: "100%" },
                }}
                step={stepSize}
                stepHoldDelay={300}
                stepHoldInterval={100}
                onBlur={() => handleIntervalChange()}
              />

              <NumberInput
                withAsterisk
                label="Max Duration"
                maxLength={8}
                {...form.getInputProps("maxDuration")}
                disabled={isFormActive}
                styles={{ root: { width: "100%" } }}
                step={stepSize}
                stepHoldDelay={300}
                stepHoldInterval={100}
                min={stepSize}
                onBlur={() => handleIntervalChange()}
              />
            </Flex>

            <Flex justify={"space-between"}>
              <Switch
                labelPosition="left"
                label="Taxable"
                checked={!!form.getInputProps("isTaxable")?.value}
                {...form.getInputProps("isTaxable")}
                mt={"sm"}
              />
              {isTaxableChecked && (
                <Switch
                  labelPosition="left"
                  label="Post-Tax"
                  checked={!!form.getInputProps("isPostTax")?.value}
                  {...form.getInputProps("isPostTax")}
                  mt={"sm"}
                />
              )}
            </Flex>
            <MultiSelect
              disabled={isFormActive}
              data={
                policiesArr?.policyMany?.map((val) => ({
                  value: val.id,
                  label: val.name,
                })) || []
              }
              label="Policies"
              {...form.getInputProps("policies")}
            />
          </Grid.Col>
        </Grid>

        <Flex gap={"xs"}>
          <Select
            withAsterisk
            data={revenueArr}
            disabled={isFormActive}
            label="Revenue Category"
            {...form.getInputProps("revenueCategory")}
            styles={{ root: { width: "100%" } }}
          />

          <Select
            withAsterisk
            data={serviceArr}
            disabled={isFormActive}
            label="Service Type"
            {...form.getInputProps("serviceType")}
            styles={{ root: { width: "100%" } }}
          />
        </Flex>

        <Textarea
          label="Description"
          {...form.getInputProps("description")}
          disabled={isFormActive}
        />

        {!isEdit && (
          <Group position="apart" mt="md" style={{ marginTop: 30 }}>
            <Button
              radius="md"
              variant="outline"
              onClick={() => modals.closeAll()}
              style={{
                flex: 1,
                color: theme.colors.secondaryTextColor[6],
              }}
              sx={{
                backgroundColor: theme.colors.secondary[6],
                "&:hover": {
                  background: theme.colors.secondary[5],
                },
              }}
            >
              Cancel
            </Button>
            <Button
              radius="md"
              type="submit"
              style={{
                flex: 1,
              }}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
              loading={createLoading}
            >
              Create
            </Button>
          </Group>
        )}
      </FormCard>

      {sessionId && (
        <>
          <ActionCard
            title="Durations"
            mt={"sm"}
            handleModal={isAllowWrite ? handleModalDuration : undefined}
            hideModal={!durationsData?.length}
          >
            {data?.privateSessionOne?.durations.map((e) => (
              <DurationDetail
                visibleCards={visibleCards}
                setvisibleCards={setvisibleCards}
                key={e.id}
                sessionData={data?.privateSessionOne || undefined}
                title={String(e.duration)}
                durationId={e.id}
                currentDuration={{
                  value: String(e.duration),
                  label: String(e.duration),
                }}
                durationsData={durationsData}
                isAllowWrite={isAllowWrite}
              />
            ))}
          </ActionCard>

          <ActionCard
            title="Instructors"
            mt={"sm"}
            hideModal={!isEmpChecked}
            handleModal={isAllowWrite ? handleModal : undefined}
            rightIconButton={
              <>
                <Switch
                  disabled={!isAllowWrite}
                  checked={isEmpChecked}
                  {...form.getInputProps("isEmployeeRequired")}
                  onChange={() => {
                    setshowLoader(false);
                    form.setFieldValue("isEmployeeRequired", !isEmpChecked);
                    handleSubmit({
                      ...form.values,
                      isEmployeeRequired: !isEmpChecked,
                    });
                  }}
                  hasBackground={true}
                />
              </>
            }
          >
            {isEmpChecked &&
              CurrentInstructors.map((e) => (
                <InstructorDetail
                  visibleCards={visibleCards}
                  setvisibleCards={setvisibleCards}
                  key={e.id}
                  sessionData={data?.privateSessionOne || undefined}
                  title={`${e.employee.user.firstName} ${e.employee.user.lastName}`}
                  overrideId={e.id}
                  currentEmployee={{
                    value: e.employee.id,
                    label: `${e.employee.user.firstName} ${e.employee.user.lastName}`,
                  }}
                  isAllowWrite={isAllowWrite}
                  isLocationSession={isLocationSession}
                />
              ))}
          </ActionCard>

          <ActionCard
            title={isLocationSession ? "Rooms" : "Locations"}
            mt={"sm"}
            handleModal={
              isAllowWrite && showAddLocation ? handleModalRoom : undefined
            }
          >
            {CurrentRooms.map((e) => (
              <RoomDetail
                visibleCards={visibleCards}
                setvisibleCards={setvisibleCards}
                key={e.id}
                sessionData={data?.privateSessionOne || undefined}
                title={e.location.name}
                locationRoomId={e.id}
                currentLocation={{
                  value: e.location.id,
                  label: e.location.name,
                }}
                isAllowWrite={isAllowWrite}
                isOrg={!isLocationSession}
              />
            ))}
          </ActionCard>
        </>
      )}
    </>
  );
};

export default memo(PrivateSessionDetail);
